/**
 * Chunk D — Welcome + dashboard tour.
 *
 * The very last onboarding step. Renders a role-aware tour (admins see
 * approvals/access/settings, sales see clients/incentives/downloads) and
 * a Finish button that POSTs /auth/complete-onboarding. On success the
 * server flips onboardingStep → complete, we refresh /me, and route via
 * `routeForStep(u)` — which will land them on /admin/dashboard or the
 * marketing landing page.
 *
 * Keep this page intentionally simple: it should feel like a victory lap,
 * not another form.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Sparkles, ArrowRight, LayoutDashboard, Users, KeySquare, Settings,
    BadgeCheck, Wallet, Folder, ListChecks, CheckCircle2, Trophy,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth, routeForStep } from '../../store/auth';

/* Tour cards per role-bucket. Admin/manager get the back-office surface;
   everyone else gets the field-facing surface. We keep the lists short —
   the goal is "show them the 4 most useful doors", not a sitemap. */
const TOUR_ADMIN = [
    { icon: LayoutDashboard, title: 'Dashboard',  body: 'Live KPIs, approvals queue, and the clients chart.' },
    { icon: BadgeCheck,      title: 'Approvals',  body: 'New signups land here for designation + role review.' },
    { icon: KeySquare,       title: 'Access',     body: 'Per-user permissions and custom roles in one place.' },
    { icon: Settings,        title: 'Settings',   body: 'Theme, finance, onboarding terms — everything tweakable.' },
];

const TOUR_SALES = [
    { icon: LayoutDashboard, title: 'Dashboard',     body: 'Your pipeline at a glance — pending, won, this month.' },
    { icon: Users,           title: 'Clients',       body: 'Add new submissions; the ledger tracks them end-to-end.' },
    { icon: Wallet,          title: 'Incentives',    body: 'Track quota progress and projected payouts.' },
    { icon: Folder,          title: 'Downloads',     body: 'Brand assets, scripts, training PDFs in one library.' },
];

export default function Welcome() {
    const user   = useAuth(s => s.user);
    const loadMe = useAuth(s => s.loadMe);
    const nav    = useNavigate();
    const [busy, setBusy] = useState(false);

    const isAdminish = ['admin', 'manager'].includes(user?.role);
    const tour = isAdminish ? TOUR_ADMIN : TOUR_SALES;

    const finish = async () => {
        setBusy(true);
        try {
            await api.post('/auth/complete-onboarding');
            toast.success('Welcome aboard ✓');
            const u = await loadMe();
            nav(routeForStep(u));
        } catch {/* toasted by interceptor */}
        finally { setBusy(false); }
    };

    /* The five steps the user just cleared. We render them as checkmarks
       so the moment feels celebratory, not transactional. */
    const cleared = [
        'Email verified',
        'Admin approval',
        'KRA accepted',
        'KPI accepted',
        'Training quiz passed',
    ];

    return (
        <div className="space-y-7">
            <div className="text-center">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-brand-yellow to-brand-orange text-white grid place-items-center mx-auto shadow-card">
                    <Trophy className="h-8 w-8" />
                </div>
                <h1 className="text-3xl font-extrabold mt-4 tracking-tight">
                    You're all set, {user?.name?.split(' ')[0] || 'friend'} <Sparkles className="inline h-6 w-6 text-brand-yellow align-baseline" />
                </h1>
                <p className="text-sm text-ink-500 mt-2 max-w-md mx-auto">
                    Onboarding is done. Here's a 30-second tour of where you'll spend your time, then you can dive in.
                </p>
            </div>

            {/* Cleared-step trail */}
            <div className="ag-card p-5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500 mb-3">What you finished</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                    {cleared.map((s) => (
                        <li key={s} className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                            <span>{s}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Role-aware tour cards */}
            <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500 mb-2">
                    {isAdminish ? 'For admins — your daily surface' : 'For your role — your daily surface'}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tour.map((t) => (
                        <div key={t.title} className="ag-card p-4 flex gap-3 hover:shadow-soft transition">
                            <span className="h-10 w-10 rounded-lg bg-brand-teal-50 text-brand-teal-700 grid place-items-center shrink-0">
                                <t.icon className="h-5 w-5" />
                            </span>
                            <div className="min-w-0">
                                <div className="font-bold text-sm">{t.title}</div>
                                <p className="text-xs text-ink-500 mt-0.5 leading-relaxed">{t.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* One more nudge */}
            <div className="ag-card p-5 bg-brand-teal-50/40 border border-brand-teal/20">
                <div className="flex gap-3 items-start">
                    <ListChecks className="h-5 w-5 text-brand-teal-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-ink-700 leading-relaxed">
                        <b>Quick tip:</b> the {isAdminish ? 'Approvals' : 'Clients'} page is the page you'll open most. Bookmark it
                        from the sidebar after you land. The bell icon in the header surfaces anything that needs your attention.
                    </div>
                </div>
            </div>

            <button
                className="ag-btn-primary w-full justify-center py-3"
                disabled={busy}
                onClick={finish}
            >
                {busy ? 'Unlocking your app…' : <>Enter AGEON <ArrowRight className="h-4 w-4" /></>}
            </button>

            <p className="text-[11px] text-ink-500 text-center">
                You can always replay this tour from your profile menu later.
            </p>
        </div>
    );
}
